/* eslint-disable ember/no-get */
import { A } from '@ember/array';
import type NativeArray from '@ember/array/-private/native-array';
import type ArrayProxy from '@ember/array/proxy';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import EmberObject, {
  computed,
  get,
  getProperties,
  set,
  setProperties,
  action,
  notifyPropertyChange,
} from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { reads, sort } from '@ember/object/computed';
import type RouterService from '@ember/routing/router-service';
import { cancel, later, run, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { underscore, camelize } from '@ember/string';
import { buildWaiter, waitFor } from '@ember/test-waiters';
import { isEmpty, isPresent } from '@ember/utils';
import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import type { Task, TaskInstance } from 'ember-concurrency';
import { didCancel, dropTask, rawTimeout, restartableTask, task, all } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import { resource, use } from 'ember-resources';
import type { DocumentBusyKind } from 'garaje/components/visitors/document-review-card';
import config from 'garaje/config/environment';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type DocumentUploadPageModel from 'garaje/models/document-upload-page';
import type EmployeeModel from 'garaje/models/employee';
import type EmployeeScreeningFlowModel from 'garaje/models/employee-screening-flow';
import FlowModel from 'garaje/models/flow';
import type GlobalFlowModel from 'garaje/models/global-flow';
import type InviteModel from 'garaje/models/invite';
import type LocationModel from 'garaje/models/location';
import type RecurringInvite from 'garaje/models/recurring-invite';
import RecurringRule from 'garaje/models/recurring-rule';
import type { VisualComplianceReport } from 'garaje/models/screener-result';
import type ScreenerResult from 'garaje/models/screener-result';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type UserModel from 'garaje/models/user';
import type UserDatum from 'garaje/models/user-datum';
import type UserDocumentAttachmentModel from 'garaje/models/user-document-attachment';
import type UserDocumentTemplateAttachmentModel from 'garaje/models/user-document-template-attachment';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import type { InviteDraftDocument, InviteSaveAdapterOptions } from 'garaje/serializers/invite';
import type ActiveStorageService from 'garaje/services/active-storage-extension';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type ReactRendererService from 'garaje/services/react-renderer';
import { isSensitiveFieldMasked, type SensitiveState } from 'garaje/services/sensitive-fields-visibility';
import type SensitiveFieldsVisibilityService from 'garaje/services/sensitive-fields-visibility';
import type StateService from 'garaje/services/state';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import { createBlocklistFilter, saveBlocklistFilter } from 'garaje/utils/blocklist-utils';
import { shouldClearRecurringForFlow } from 'garaje/utils/clear-recurring-for-ava-flow';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { RECURRING_OPTIONS, PURPOSE_OF_VISIT } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { arrivalChangeSkipsReevaluation, canTriggerWorkflowReeval } from 'garaje/utils/invite-workflow-reeval';
import isResolvedIdNameMismatch from 'garaje/utils/is-resolved-id-name-mismatch';
import { mediaQuery } from 'garaje/utils/media-query';
import type { ScopedSubLookup } from 'garaje/utils/mpb-location-eligibility';
import {
  isFlowEligibleForLocation,
  isLocationEligibleFromScopedMap,
  MPB_FEATURE_FLAG,
} from 'garaje/utils/mpb-location-eligibility';
import { fetchPolicyDraft } from 'garaje/utils/policy-draft';
import type { PolicyDraftResponse } from 'garaje/utils/policy-draft';
import addOrUpdateUserData from 'garaje/utils/user-data';
import buildFlowFieldValidations from 'garaje/validations/flow-field';
import { bool } from 'macro-decorators';
import moment from 'moment-timezone';
import type { Root } from 'react-dom/client';
import { cached, localCopy } from 'tracked-toolbox';
import type { ValueOf } from 'type-fest';

const testWaiter = buildWaiter('invites-single-prereg-component-waiter');
const isTesting = config.environment === 'test';
// Grace window for a queued field-values PATCH to drain before a submit proceeds —
// generous for a single PATCH round-trip, far below a file-upload duration.
const DOCUMENT_SAVE_DRAIN_GRACE_MS = 2000;

interface InvitesSinglePreregComponentSignature {
  Args: {
    blocklistContacts: UserModel[];
    config: Record<string, unknown>;
    dateChanged: (date: Date) => void;
    flows: NativeArray<FlowModel>;
    hostFieldIsRequired: boolean;
    invite: InviteModel;
    isEditInvite: boolean;
    isMultiLocationInvite: boolean;
    inviteChangeset: DetailedChangeset<InviteModel>;
    legalDocumentDescriptions: Record<string, unknown>;
    locationIsConnectedToProperty: boolean;
    locations: NativeArray<LocationModel>;
    modelDidPartialSave: (invite: InviteModel | DetailedChangeset<InviteModel>, error?: unknown) => void;
    modelDidSave: (
      date?: Date,
      goToIndex?: boolean,
      inviteAttrs?: Partial<InviteModel>,
      options?: { addAnother?: boolean },
    ) => void;
    onFlowChanged: (flow: string, inviteChangeset?: DetailedChangeset<InviteModel>) => void;
    onLocationSelectedTask?: Task<unknown, [LocationModel]>;
    shouldShowLegalDocumentDescriptions: boolean;
    shouldShowRecurrence: boolean;
    hasAttachments?: boolean;
    requiredDocumentTemplates?: CustomUserDocumentTemplateModel[];
    reloadDocuments?: () => void;
    signInFields?: ArrayProxy<SignInFieldModel>;
  };
}

interface SaveOptions {
  addAnother?: boolean;
  whichInvites?: ValueOf<typeof RECURRING_OPTIONS>;
  workflowReevaluationReason?: string | null;
}

/**
 * Form for creating or editing an invite
 */
type SecondaryLocationOption = { location: LocationModel; eligible: boolean; disabled: boolean };
type FlowOption = { flow: FlowModel; eligible: boolean; disabled: boolean };

export default class InvitesSinglePreregComponent extends Component<InvitesSinglePreregComponentSignature> {
  private root?: Root;
  private pollTimer?: ReturnType<typeof later>;
  private pollSignInFieldsTimer?: ReturnType<typeof later>;
  private freshSignInFields: SignInFieldModel[] | undefined = undefined;

  @service declare abilities: AbilitiesService;
  // ActiveStorage service: https://github.com/algonauti/ember-active-storage
  @service declare activeStorageExtension: ActiveStorageService;
  @service declare ajax: AjaxService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare state: StateService;
  @service declare uploaderFactory: UploaderFactoryService;
  @service declare reactRenderer: ReactRendererService;
  @service declare sensitiveFieldsVisibility: SensitiveFieldsVisibilityService;

  // Below `lg` (64em) the app shell hides its nav; editing an existing invite falls back to read-only there.
  // em to match the nav's `to-lg` breakpoint, so read-only can't disagree with nav visibility.
  @use isLgUp = mediaQuery('(min-width: 64em)');

  @localCopy('args.invite.employee') owner!: EmployeeModel;
  @localCopy('args.invite.sendGuestEmail') sendGuestEmail!: InviteModel['sendGuestEmail'];
  @localCopy('args.config', {}) config!: Record<string, unknown>;
  // Plain getter, not @localCopy: @localCopy writes to a tracked meta value
  // on first read whenever args.flows changes, which can backtrack Glimmer
  // if it changes mid-render.
  get flows(): NativeArray<FlowModel> {
    return this.args.flows ?? A<FlowModel>();
  }
  @localCopy('args.hasAttachments', false) hasAttachments!: boolean;
  @tracked confirmRemovalOfDocument: VisitorDocumentModel | null = null;
  @tracked flowToChangeTo?: FlowModel;
  @tracked inviteChanges: unknown;
  @tracked isShowingEditRecurringInvite = false;
  @tracked isShowingDeleteConfirmation = false;
  @tracked sendNotificationCallback: unknown;
  @tracked editAffectsMultipleLocationsModal = false;
  @tracked showConfirmVisitorTypeChangeModal = false;
  @tracked emailHasFocus = false;
  @tracked isEmailRevealed = false;
  @tracked isShowingWorkflowReevaluationModal = false;
  // Admin pre-reg "Documents" section is expanded by default; the header's
  // Collapse/Expand link toggles it (matches the Figma Documents-section header).
  @tracked adminPreRegDocumentsExpanded = true;
  @tracked workflowReevaluationReason: string | null | undefined = undefined;

  toggleAdminPreRegDocuments = (): void => {
    this.adminPreRegDocumentsExpanded = !this.adminPreRegDocumentsExpanded;
  };
  @tracked policyDraft: PolicyDraftResponse | null = null;
  @tracked isResolvingPolicyDraft = false;

  @tracked blocklistFilterInModal?: BlacklistFilter;
  @tracked activeTab: 'visit-details' | 'documents' = 'visit-details';

  // Lazily populated by loadScopedSubsForCandidatesTask. Reassigned, not mutated.
  @tracked scopedSubsByLocation: ReadonlyMap<string, ScopedSubLookup> = new Map();

  // Tracked source of truth for primary location id so flow-picker getters
  // re-fire on in-form switches. `invite.belongsTo` is tag-blind and
  // `inviteChangeset.location.id` trips PromiseBelongsTo races.
  @tracked private _primaryLocationId: string | null = null;

  constructor(owner: unknown, args: InvitesSinglePreregComponentSignature['Args']) {
    super(owner, args);
    // Prefer the changeset's pending location over the underlying record's id
    // so initial eligibility gates against what the user will actually save.
    const pendingId = <string | undefined>get(this, 'args.inviteChangeset.location.id');
    const inviteId = this.args.invite?.belongsTo('location').id();
    this._primaryLocationId = pendingId ?? inviteId ?? null;
    if (this.isWorkflowReevaluationEnabled) {
      void this.loadPolicyDraftTask.perform();
    }
  }

  loadPolicyDraftTask = dropTask(async () => {
    try {
      this.policyDraft = await fetchPolicyDraft(this.ajax, this.args.invite.id);
    } catch {
      this.policyDraft = null;
    }
  });

  // Fans out one scoped-subscription fetch per candidate location — the only
  // non-admin path that resolves the MPB location→subscription mapping.
  // restartableTask cancels stale fan-outs when the flow or candidate set
  // changes; ids already in the map are skipped (dedup).
  loadScopedSubsForCandidatesTask = restartableTask(async (locationIds: readonly string[]) => {
    const companyId = this.state.currentCompany?.id;
    if (!companyId) return;

    const next = new Map(this.scopedSubsByLocation);
    const toFetch = locationIds.filter((id) => id && !next.has(id));
    if (toFetch.length === 0) return;

    // eslint-disable-next-line ember/use-ember-get-and-set
    for (const id of toFetch) next.set(id, { state: 'loading' });
    if (this.isDestroying || this.isDestroyed) return;
    this.scopedSubsByLocation = next;

    let results: readonly (readonly [string, ScopedSubLookup])[];
    try {
      results = await Promise.all(
        toFetch.map(async (locationId): Promise<readonly [string, ScopedSubLookup]> => {
          try {
            const records = await this.store.query('scoped-subscription', {
              filter: { scope: 'mixed-plan', company_id: companyId, location_id: locationId, app: 'visitors' },
            });
            const [sub] = records.slice();
            return [
              locationId,
              {
                state: 'loaded',
                plan: sub?.plan ?? null,
                canAccessGlobalSignInFlows: sub?.canAccessGlobalSignInFlows ?? false,
              },
            ];
          } catch {
            // Fail open on any read failure here. 401 is already handled
            // centrally by `billing-v1` adapter's `handleResponse`, which
            // calls `session.invalidate()` before the error reaches us.
            // 403/5xx/network are treated the same as a missing scoped sub —
            // helpers fall back to fail-open; the backend save path remains
            // the final gate, and locking the picker to all-disabled would
            // mask transient billing outages.
            return [locationId, { state: 'error' }];
          }
        }),
      );
    } catch (e) {
      // restartableTask cancellation throws TaskCancelation at the await. Roll
      // back the 'loading' sentinels we wrote so a subsequent perform() doesn't
      // dedup-skip these ids (they'd otherwise be silently stranded as loading
      // for the component lifetime).
      if (didCancel(e) && !this.isDestroying && !this.isDestroyed) {
        const rolled = new Map(this.scopedSubsByLocation);
        for (const id of toFetch) {
          // eslint-disable-next-line ember/use-ember-get-and-set
          if (rolled.get(id)?.state === 'loading') rolled.delete(id);
        }
        this.scopedSubsByLocation = rolled;
      }
      throw e;
    }

    if (this.isDestroying || this.isDestroyed) return;
    const settled = new Map(this.scopedSubsByLocation);
    // eslint-disable-next-line ember/use-ember-get-and-set
    for (const [id, lookup] of results) settled.set(id, lookup);
    this.scopedSubsByLocation = settled;
  });

  // Reactively fetches scoped-subscription data for the selected flow's candidate locations
  // whenever the flow or primary location changes (replaces the section's did-insert/did-update
  // modifiers). A template `{{...}}` sink consumes it; the task defers past render so the fetch's
  // synchronous map write never lands mid-render (backtracking).
  @use scopedSubsAutoloader = resource<string>(() => {
    void this.selectedFlow;
    void this._primaryLocationId;
    void this.ensureScopedSubsForCandidatesTask.perform();
    return '';
  });

  ensureScopedSubsForCandidatesTask = restartableTask(async () => {
    await Promise.resolve();
    if (this.isDestroying || this.isDestroyed) return;
    this.ensureScopedSubsForCandidates();
  });

  // Fetches the primary (for flow-picker gating) and the global flow's locations (for
  // secondary-picker gating); the fetch task dedupes against ids already in the map.
  @action
  ensureScopedSubsForCandidates(): void {
    if (!this.featureFlags.isEnabled(MPB_FEATURE_FLAG)) return;
    void this.fetchScopedSubsForSelectedFlow();
  }

  // Combines primary + global-flow siblings into a single fan-out so the
  // restartableTask doesn't cancel its own first perform and strand the
  // primary as fail-open. Awaits the AsyncBelongsTo before reading hasMany.
  private async fetchScopedSubsForSelectedFlow(): Promise<void> {
    const primaryLocationId =
      this._primaryLocationId ?? <string | undefined>get(this, 'args.inviteChangeset.location.id') ?? null;
    const ids: string[] = [];
    if (primaryLocationId) ids.push(primaryLocationId);

    const selectedFlow = this.selectedFlow as FlowModel | null | undefined;
    if (selectedFlow?.globalFlow) {
      try {
        const globalFlow = (await selectedFlow.globalFlow) as GlobalFlowModel | null;
        if (this.isDestroying || this.isDestroyed) return;
        if (globalFlow) {
          // eslint-disable-next-line ember/no-get
          const locationsProxy = (await get(globalFlow, 'locations')) as
            | AsyncHasMany<SkinnyLocationModel>
            | SkinnyLocationModel[]
            | undefined;
          if (this.isDestroying || this.isDestroyed) return;
          if (locationsProxy) {
            const locations: SkinnyLocationModel[] = Array.isArray(locationsProxy)
              ? locationsProxy
              : locationsProxy.slice();
            for (const loc of locations) {
              if (loc.id !== primaryLocationId) ids.push(loc.id);
            }
          }
        }
      } catch {
        // Relationship-fetch failure: fall through with primary-only ids.
      }
    }

    if (ids.length === 0) return;
    void this.loadScopedSubsForCandidatesTask.perform(ids);
  }

  // VIS-17085: synchronous "will saving this edit re-evaluate the policy?" decision, given an
  // already-resolved policy draft. WTA makes the authoritative call at rerun — this is only a
  // UI hint, so it must NEVER throw (any error falls back to assuming re-evaluation). Callers
  // resolve the draft first (shouldOpenReevaluationModal / onFormSubmit's recurring branch).
  // Shared by the single-invite modal gate and the recurring picker's folded warning so the two
  // can't diverge — suppressing one but not the other was the bug here.
  private willReevaluateOnSave(): boolean {
    if (!this.policyDraft) return true; // unresolved draft → keep today's default (re-evaluate)
    if (!this.policyDraft.automaticReevaluation) return false;

    // Don't warn about a re-approval that won't happen: suppress when every changed field is
    // skip-configured for this policy (applies equally to approved and denied invites).
    try {
      if (this.allChangedFieldsSkipReevaluation()) return false;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[VIS-17085] skip check failed; assuming re-evaluation:', err);
    }

    return true;
  }

  async shouldOpenReevaluationModal(): Promise<boolean> {
    // Resolve the policy draft so we can read automaticReevaluation + changeHandling.
    if (!this.policyDraft && this.loadPolicyDraftTask.isRunning) {
      this.isResolvingPolicyDraft = true;
      try {
        await this.loadPolicyDraftTask.last;
      } finally {
        this.isResolvingPolicyDraft = false;
      }
    }

    return this.willReevaluateOnSave();
  }

  // VIS-17085: true iff every pending change is skip-configured (so saving won't re-evaluate).
  private allChangedFieldsSkipReevaluation(): boolean {
    if (!this.featureFlags.isEnabled('ava-skip-reevaluation-fields')) return false;

    // Custom sign-in field edits are not skip-configurable → they always re-evaluate.
    if (this.hasRealUserDataFieldChange()) return false;

    const changes = this.args.inviteChangeset.changes as Array<{ key: string }>;
    if (changes.length === 0) return false;

    return changes.every((change) => this.changeKeySkipsReevaluation(change.key));
  }

  // userDataChangesets are seeded with their existing value at build time
  // (`set(fieldChangeset, 'value', value)`), which marks every one `isDirty` regardless of
  // user edits. So compare each changeset's current value against the original invite.userData
  // value to detect an actual edit, rather than trusting isDirty.
  private hasRealUserDataFieldChange(): boolean {
    const originalUserData = this.args.invite.userData || A();
    return Object.values(this.userDataChangesets).some((fieldChangeset) => {
      const fieldName = get(fieldChangeset, 'name');
      const originalEntry = originalUserData.find((entry) => get(entry, 'field') === fieldName);
      const originalValue = originalEntry ? get(originalEntry, 'value') : undefined;
      return get(fieldChangeset, 'value') !== originalValue;
    });
  }

  private changeKeySkipsReevaluation(key: string): boolean {
    // privateNotes never re-evaluates (structurally not a rerun change kind).
    if (key === 'privateNotes') return true;
    if (key === 'expectedArrivalTime') {
      return arrivalChangeSkipsReevaluation(
        this.args.invite.expectedArrivalTime,
        this.args.inviteChangeset.expectedArrivalTime,
        this.policyDraft?.changeHandling,
      );
    }
    // Any other field is not skip-configurable.
    return false;
  }

  get isNotPendingApproval(): boolean {
    return !this.args.invite.needsApprovalReview;
  }

  get isWorkflowReevaluationEnabled(): boolean {
    return this.args.isEditInvite && canTriggerWorkflowReeval(this.args.invite);
  }

  get shouldDisableFlowForApprovedAvaInvite(): boolean {
    return this.args.invite.approved && !!this.args.invite.visitApproval?.visitWorkflowResult;
  }

  get isAvaInvitePendingReview(): boolean {
    return this.isWorkflowReevaluationEnabled && this.args.invite.needsApprovalReview;
  }

  get isResubmittingDeniedInvite(): boolean {
    return this.isWorkflowReevaluationEnabled && this.args.invite.approvalWasDenied;
  }

  get shouldShowWorkflowReevaluationModal(): boolean {
    if (!this.isWorkflowReevaluationEnabled) return false;

    // Don't trigger re-evaluation if only private notes changed
    const changes = this.args.inviteChangeset.changes as Array<{ key: string }>;
    const hasNonPrivateNoteChanges = changes.some((change) => change.key !== 'privateNotes');

    // User-data field edits (custom sign-in fields) live in their own field changesets and
    // don't propagate to inviteChangeset.changes until saveTask runs, so check them directly.
    const hasDirtyUserDataField = Object.values(this.userDataChangesets).some((fieldChangeset) =>
      get(fieldChangeset, 'isDirty'),
    );

    return hasNonPrivateNoteChanges || hasDirtyUserDataField;
  }

  @reads('args.inviteChangeset.expectedArrivalTime') minRruleUntilDate!: InviteModel['expectedArrivalTime'];
  @reads('args.inviteChangeset.expectedArrivalTime') expectedArrivalTime!: InviteModel['expectedArrivalTime'];
  @reads('args.inviteChangeset.location.timezone') timezone!: LocationModel['timezone'];
  @sort('signInFields', (a: SignInFieldModel, b: SignInFieldModel) => a.position - b.position)
  sortedSignInFields!: SignInFieldModel[];
  @reads('selectedFlow.activeUserDocumentTemplateConfigurations')
  activeUserDocumentTemplateConfigurations?: FlowModel['activeUserDocumentTemplateConfigurations'];
  @bool('args.inviteChangeset.change.visitorDocuments') isVisitorDocumentsModified!: boolean;

  get recurringInvite(): RecurringInvite | undefined {
    return <RecurringInvite>this.args.invite.belongsTo('recurringInvite').value();
  }

  get hasParentRecurringGroup(): boolean {
    return Boolean(this.recurringInvite?.belongsTo('recurringGroupInvite').id());
  }

  get canInviteAgain(): boolean {
    return (
      !this.args.invite.isFromEmployeeScreening &&
      !this.args.invite.isFromWalkUp &&
      !this.args.invite.isNew &&
      !this.args.invite.approvalWasDenied &&
      !this.args.invite.needsApprovalReview &&
      !this.args.invite.anonymizedAt
    );
  }

  get visualComplianceReport(): VisualComplianceReport[] | undefined {
    const { invite } = this.args;

    const visualComplianceResult = invite.visitApproval?.visualComplianceResult;
    return visualComplianceResult ? [visualComplianceResult.report] : undefined;
  }

  get noVisualComplianceMatch(): boolean {
    if (!this.args.invite.visitApproval?.visualComplianceResult) return false;
    return !this.args.invite.visitApproval.visualComplianceResult.didFail;
  }

  get errorVisualComplianceCall(): boolean {
    const callStatus = this.args.invite.visitApproval?.visualComplianceResult?.report['call-status'];
    return callStatus === 'unreachable' || callStatus === 'error';
  }

  get showSidePanel(): boolean {
    const approval = this.args.invite.visitApproval;

    return Boolean(
      approval ||
        this.args.shouldShowLegalDocumentDescriptions ||
        this.args.invite.platformJobs ||
        this.showDetailsPanel,
    );
  }

  get showDetailsPanel(): boolean {
    return Boolean(
      this.args.shouldShowLegalDocumentDescriptions ||
        this.args.locationIsConnectedToProperty ||
        this.shouldShowGroupName ||
        this.visualComplianceReport ||
        this.shouldShowQuestionnairesStatus ||
        this.detailedResults.length ||
        this.hasIdNameMismatchResolution ||
        this.args.invite.platformJobs,
    );
  }

  get hasIdNameMismatchResolution(): boolean {
    return this.featureFlags.isEnabled('id-name-mismatch-screener') && isResolvedIdNameMismatch(this.args.invite);
  }

  get detailedResults(): ScreenerResult[] {
    return (
      this.args.invite.visitApproval?.screenerResults.filter(
        (result) => result.details && !result.isVisualCompliance(),
      ) ?? []
    );
  }

  get canAccessMultipleHosts(): boolean {
    return this.state.features?.canAccessMultipleHosts ?? false;
  }

  get canDelete(): boolean {
    return this.abilities.can('delete invite', this.args.invite) && this.isNotPendingApproval;
  }

  get canEdit(): boolean {
    const { isEditInvite, invite } = this.args;

    if (invite.anonymizedAt) return false;

    // Editing an existing invite is read-only on phone + tablet, where the shell nav is hidden.
    if (isEditInvite && !this.isLgUp) return false;

    // If editing an existing invite, check permissions
    // If creating an invite, always allow "editing"
    return isEditInvite ? this.abilities.can('edit invite', invite) : true;
  }

  get canReview(): boolean {
    const { isEditInvite, invite } = this.args;

    // If editing an existing invite, check permissions
    // If creating an invite, always allow "editing"
    return isEditInvite
      ? this.abilities.can('review entry-approval', {
          context: 'location',
          visitApproval: this.args.invite.visitApproval,
          parent: invite,
        })
      : true;
  }

  get cannotReview(): boolean {
    return !this.canReview;
  }

  get secondaryLocations(): AsyncHasMany<LocationModel> | LocationModel[] {
    return this.args.inviteChangeset.childInviteLocations ?? [];
  }

  get emailSignInField(): SignInFieldModel | undefined {
    return this.signInFields.find((field) => field.isEmail);
  }

  get isEmailRequired(): boolean {
    if (
      !(
        this.featureFlags.isEnabled('visitors-required-email-field') ||
        this.locationFeatureFlags.isEnabled('visitors-required-email-field-by-location')
      )
    ) {
      return false;
    }

    return !!this.emailSignInField?.required;
  }

  get canSetPropertyNotes(): boolean {
    // "property notes" field only appears if the current location is connected to a property.
    // It should also only be shown to someone who can edit the invite (e.g. Global Admin, Location Admin,
    // Front Desk Admins, or the employee who created the invite).
    if (!this.args.locationIsConnectedToProperty) {
      return false;
    }
    if (<boolean>(<unknown>this.args.invite.isNew)) {
      return true;
    }
    return this.canEdit;
  }

  get hasRecurringInvite(): boolean {
    return isPresent(this.args.invite.belongsTo('recurringInvite').id());
  }

  get hasDatetimeChange(): boolean {
    return isPresent(this.args.inviteChangeset.changes.find((change) => change['key'] === 'expectedArrivalTime'));
  }

  get hasEmailChange(): boolean {
    return isPresent(this.args.inviteChangeset.changes.find((change) => change['key'] === 'email'));
  }

  get shouldSendNotification(): boolean {
    return !this.args.invite.isNew && !this.hasEmailChange && this.hasDatetimeChange;
  }

  get disabledRecurringOptions(): { THIS: boolean } | null {
    return this.isVisitorDocumentsModified ? { THIS: true } : null;
  }

  get defaultRecurringOption(): string {
    return this.isVisitorDocumentsModified ? RECURRING_OPTIONS.THIS_AND_FOLLOWING : RECURRING_OPTIONS.THIS;
  }

  get location(): AsyncBelongsTo<LocationModel> {
    return this.args.invite?.location;
  }

  get shouldShowSecondaryLocations(): boolean {
    if (!this.state.features?.canAccessMultiLocationInvites) return false;
    if (this.isMultiLocationEdit) return false;
    return this.args.locations?.length > 1;
  }

  get shouldShowQuestionnairesStatus(): boolean {
    if (!this.state.features?.canAccessQuestionnaires) return false;
    return (
      <number>this.args.invite.flow?.content?.flowQuestionnaires.length > 0 ||
      <number>this.args.invite.questionnaireResponses.length > 0
    );
  }

  get visitWorkflowEnabled(): boolean {
    return (<FlowModel>this.selectedFlow)?.visitWorkflowEnabled ?? false;
  }

  get recurringInvitesOnAvaEnabled(): boolean {
    return this.featureFlags.isEnabled('ava-recurring-invites-no-reeval');
  }

  get recurringInviteOneOffSplitEnabled(): boolean {
    return this.featureFlags.isEnabled('recurring-invite-one-off-split');
  }

  // Behind the split flag (flag OFF keeps the legacy picker with no warning). Gated on re-run
  // state, not the picked scope: under re-run every scope resubmits. willReevaluateOnSave()
  // also suppresses the warning when the arrival-only edit is skip-configured (VIS-17085) —
  // onFormSubmit resolves the draft before opening the picker so this reads accurately.
  get shouldShowRecurringReevaluationWarning(): boolean {
    return (
      this.recurringInviteOneOffSplitEnabled && this.shouldShowWorkflowReevaluationModal && this.willReevaluateOnSave()
    );
  }

  // "This" routes to a single-occurrence split (a new RecurringInvite) only under the flag.
  #isOneOffSplit(whichInvites: SaveOptions['whichInvites']): boolean {
    return whichInvites === RECURRING_OPTIONS.THIS && this.recurringInviteOneOffSplitEnabled;
  }

  // The occurrence's original day (location tz). The API pins the split to this date; the host
  // may change the time but not the day.
  get splitOccurrenceDate(): string {
    const original = this.args.invite.expectedArrivalTime;
    if (!original) return '';
    return (this.timezone ? moment(original).tz(this.timezone) : moment(original)).format('YYYY-MM-DD');
  }

  get isMultiLocationEdit(): boolean {
    return this.args.isEditInvite && this.args.isMultiLocationInvite;
  }

  get showEditAffectsMultipleLocationsModal(): boolean {
    return this.args.isEditInvite && this.args.isMultiLocationInvite && this.editAffectsMultipleLocationsModal;
  }

  get locationNames(): string[] {
    return this.args.invite.locationNames ?? [];
  }

  get secondaryLocationNames(): string {
    const childLocationNames = this.locationNames.slice(1);
    return childLocationNames.join(', ');
  }

  get primaryLocationName(): string | undefined {
    const [parentLocationName, ...childLocationNames] = this.locationNames;
    return childLocationNames.length ? parentLocationName : '';
  }

  // @cached: derive `optionIndex` from a stable snapshot so selected
  // wrappers stay identity-equal to their option-list counterparts.
  @cached
  get optionsForSecondaryLocations(): SecondaryLocationOption[] {
    const { selectedFlow } = this;

    if (!selectedFlow) return [];

    const primaryLocationId = this._primaryLocationId ?? undefined;
    const globalFlow = <GlobalFlowModel>(get(selectedFlow, 'globalFlow.content') ?? get(selectedFlow, 'globalFlow'));

    if (typeof globalFlow?.hasMany !== 'function') return [];

    // `get` consumes the relationship tag; `.hasMany().ids()` does not.
    // Pair them so membership changes re-invalidate this getter.
    get(globalFlow, 'locations');
    const locationIds = globalFlow.hasMany('locations').ids();
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);

    return locationIds
      .map((locId) => this.store.peekRecord('location', locId))
      .filter((loc): loc is LocationModel => loc !== null && loc.id !== primaryLocationId)
      .map((location) => {
        const eligible = isLocationEligibleFromScopedMap(location.id, isMpbEnabled, this.scopedSubsByLocation);
        return { location, eligible, disabled: !eligible };
      });
  }

  @cached
  private get optionIndex(): Readonly<Record<string, SecondaryLocationOption>> {
    const index: Record<string, SecondaryLocationOption> = {};
    for (const option of this.optionsForSecondaryLocations) {
      index[option.location.id] = option;
    }
    return index;
  }

  // @cached so the fallback wrapper for saved-but-out-of-membership
  // locations has stable identity across renders — without this,
  // PowerSelectMultiple's `===` is-selected check fails and chips
  // flicker every render.
  @cached
  get selectedSecondaryLocationOptions(): SecondaryLocationOption[] {
    const selected = (this.args.inviteChangeset.childInviteLocations ?? []) as LocationModel[];
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    // Reuse the option-list wrapper so PowerSelect's `===` is-selected
    // check matches. Fallback covers saved invites whose location is no
    // longer in the global flow's membership — compute eligibility from
    // the scoped-sub map directly so chip styling matches the option.
    return selected.map((location) => {
      const cached = this.optionIndex[location.id];
      if (cached && cached.location === location) return cached;
      const eligible = isLocationEligibleFromScopedMap(location.id, isMpbEnabled, this.scopedSubsByLocation);
      return { location, eligible, disabled: !eligible };
    });
  }

  @computed('args.invite.creator.id', 'currentAdmin.user.id')
  get isCreator(): boolean {
    const creatorId = this.args.invite.belongsTo('creator').id();
    const currentAdminId = this.currentAdmin.user?.id;

    return Boolean(creatorId && currentAdminId && creatorId === currentAdminId);
  }
  // eslint-disable-next-line ember/require-computed-property-dependencies
  @computed('args.invite.hasAdditionalGuests', 'selectedFlow.additionalGuests')
  get displayAdditionalGuests(): boolean {
    if (this.args.invite.hasAdditionalGuests) {
      return true;
    }

    return Boolean(
      this.state.features?.canAccessGroupSignIn &&
        this.selectedFlow &&
        'additionalGuests' in this.selectedFlow &&
        this.selectedFlow.additionalGuests,
    );
  }

  @cached
  private get primaryLocationForEligibility(): LocationModel | null {
    return this._primaryLocationId ? this.store.peekRecord('location', this._primaryLocationId) : null;
  }

  @cached
  get flowOptions(): FlowOption[] {
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    const primaryLocation = this.primaryLocationForEligibility;
    // Defensive A() — `@localCopy('args.flows', [])` falls back to a plain
    // JS array when args.flows is undefined during loading; rejectBy/sortBy
    // are NativeArray-only.
    return A(this.flows)
      .rejectBy('isProtect')
      .sortBy('position')
      .map((flow) => {
        const eligible = isFlowEligibleForLocation(
          flow,
          primaryLocation,
          isMpbEnabled,
          this.state,
          this.scopedSubsByLocation,
          this._primaryLocationId,
        );
        return { flow, eligible, disabled: !eligible };
      });
  }

  @cached
  private get flowOptionIndex(): Readonly<Record<string, FlowOption>> {
    const index: Record<string, FlowOption> = {};
    for (const option of this.flowOptions) {
      index[option.flow.id] = option;
    }
    return index;
  }

  @cached
  get selectedFlowOption(): FlowOption | undefined {
    const flow = this.selectedFlow as FlowModel | null | undefined;
    if (!flow || !('id' in flow) || !flow.id) return undefined;

    // Match by id, not reference — Ember Data may re-instantiate a record
    // and break `===` while leaving the id stable.
    const cached = this.flowOptionIndex[flow.id];
    if (cached && cached.flow.id === flow.id) return cached;

    // Fallback for a saved flow no longer in the picker (e.g., the
    // employee lost access). Compute eligibility against the current
    // primary so the chip's disabled state matches.
    const isMpbEnabled = this.featureFlags.isEnabled(MPB_FEATURE_FLAG);
    const eligible = isFlowEligibleForLocation(
      flow,
      this.primaryLocationForEligibility,
      isMpbEnabled,
      this.state,
      this.scopedSubsByLocation,
      this._primaryLocationId,
    );
    return { flow, eligible, disabled: !eligible };
  }

  @computed(
    'args.{invite.flow.id,invite.flowName,invite.isDestroying,inviteChangeset.flow.content,inviteChangeset.flow.id}',
    'flows.@each.{id,name}',
    'purposeOfVisit.value',
    'state.currentLocation.employeeScreeningFlow.id',
  )
  get selectedFlow(): EmployeeScreeningFlowModel | FlowModel | Partial<FlowModel> | null | undefined {
    // the flow rel is always correct and might not match the flowName or purpose of visit
    let flowId = get(this.args, 'inviteChangeset.flow.id') || get(this.args, 'invite.flow.id');

    if (this.args.invite.isDestroying) return null;

    flowId = flowId || this.args.invite.belongsTo('flow').id();

    if (!flowId) return null;

    if (this.purposeOfVisit.value === PURPOSE_OF_VISIT.EMPLOYEE_REGISTRATION) {
      return this.state.currentLocation.employeeScreeningFlow;
    }

    const foundFlow = this.flows.find((f) => f.id === flowId) || this.store.peekRecord('flow', <string>flowId);

    if (foundFlow) return foundFlow;
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.args.inviteChangeset.flow)
      return 'content' in this.args.inviteChangeset.flow
        ? this.args.inviteChangeset.flow.content
        : this.args.inviteChangeset.flow;
    if (this.args.invite.flowName) return { name: this.args.invite.flowName };

    return;
  }

  // eslint-disable-next-line ember/require-computed-property-dependencies -- freshSignInFields is a plain property busted manually via notifyPropertyChange
  @computed(
    'args.{signInFields.[],inviteChangeset.location.id}',
    'selectedFlow.{id,signInFieldPage.signInFields.@each.name,signInFieldPage.signInFields.isSettled}',
    'state.currentLocation.id',
  )
  get signInFields(): SignInFieldModel[] {
    if (this.freshSignInFields?.length) {
      return this.freshSignInFields;
    }

    if (this.args.signInFields) {
      const fields = this.args.signInFields.slice();
      if (fields.length) {
        return fields;
      }
    }

    if (!this.selectedFlow) return [];

    const selectedFlow = <FlowModel>this.selectedFlow;
    let flowWithFields: FlowModel = selectedFlow;
    let signInFields: SignInFieldModel[] = [];

    try {
      // If the selected flow is a global flow, it shares a single set of sign-in fields with its
      // global child siblings. Scan the siblings for the sign-in fields to render.
      if (selectedFlow.isGlobalChild) {
        const globalChildSiblings: FlowModel[] = selectedFlow.globalChildSiblings;

        for (const child of globalChildSiblings) {
          // Add null checks for child flow and its relationships
          if (!child || child.isDestroyed || child.isDestroying) continue;

          try {
            const childFields: SignInFieldModel[] =
              (<AsyncHasMany<SignInFieldModel> | undefined>(
                get(child, 'signInFieldPage.signInFields')
              ))?.content?.slice() ?? [];

            if (childFields.length) {
              flowWithFields = child;
              signInFields = childFields;
              break;
            }
          } catch (childError) {
            this.metrics.trackEvent('GlobalChild Flow Warning', {
              event: 'sibling_field_access_error',
              childFlowId: child.id,
              error: childError instanceof Error ? childError.message : 'Unknown error',
            });
            continue;
          }
        }
      } else {
        signInFields =
          (<AsyncHasMany<SignInFieldModel> | undefined>(
            get(selectedFlow, 'signInFieldPage.signInFields')
          ))?.content?.slice() ?? [];
      }

      if (signInFields?.length) {
        const flowId = <string>(get(this.selectedFlow, 'id') ?? get(this.selectedFlow, 'content.id') ?? 'unknown');
        const pageId = <string>(get(flowWithFields, 'signInFieldPage.id') ?? 'unknown');
        this.metrics.trackEvent('Sign-in Fields Loaded', {
          flowId,
          pageId,
          totalFields: signInFields.length,
          fieldNames: signInFields.map((field) => field.name),
        });
      }
    } catch (error) {
      this.metrics.trackEvent('Sign-in Fields Error', {
        event: 'loading_error',
        flowId: selectedFlow?.id || 'unknown',
        flowType: selectedFlow?.type || 'unknown',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }

    return signInFields;
  }

  // The guest-name field's display name is the key used in both `userData` and
  // `sensitiveFields`, and the shared reveal key for the header/monogram.
  get nameFieldKey(): string | undefined {
    // TEMP debug (VIS-17248 name)
    // eslint-disable-next-line no-console
    console.log('[name-debug]', {
      fields: this.signInFields.map((f) => ({ name: f.name, identifier: f.identifier, isGuestName: f.isGuestName })),
      sensitiveFields: this.args.invite.sensitiveFields,
      fullName: this.args.invite.fullName,
    });
    return this.signInFields.find((field) => field.isGuestName)?.name;
  }

  get nameSensitiveState(): SensitiveState | undefined {
    const key = this.nameFieldKey;
    return key ? this.args.invite.sensitiveFields?.[key] : undefined;
  }

  get isNameSensitive(): boolean {
    return Boolean(this.nameSensitiveState);
  }

  get isNameRevealable(): boolean {
    return this.nameSensitiveState === 'visible';
  }

  get isNameRevealed(): boolean {
    const key = this.nameFieldKey;
    return key ? this.sensitiveFieldsVisibility.isRevealed(key) : false;
  }

  get isNameMasked(): boolean {
    const key = this.nameFieldKey;
    return isSensitiveFieldMasked(
      this.nameSensitiveState,
      key ? this.sensitiveFieldsVisibility.isRevealed(key) : false,
    );
  }

  get nameDisplayValue(): string {
    return this.isNameMasked ? 'Hidden' : this.args.invite.fullName;
  }

  toggleNameRevealed = (): void => {
    if (this.nameFieldKey) this.sensitiveFieldsVisibility.toggle(this.nameFieldKey);
  };

  get emailSensitiveState(): SensitiveState | undefined {
    const key = this.emailSignInField?.name;
    return key ? this.args.invite.sensitiveFields?.[key] : undefined;
  }

  get isEmailSensitive(): boolean {
    return Boolean(this.emailSensitiveState);
  }

  get isEmailRevealable(): boolean {
    return this.emailSensitiveState === 'visible' && !isEmpty(this.args.inviteChangeset.email);
  }

  get isEmailMasked(): boolean {
    if (this.emailSensitiveState === 'hidden') {
      return true;
    }
    return this.isEmailRevealable && !this.isEmailRevealed;
  }

  toggleEmailRevealed = (): void => {
    this.isEmailRevealed = !this.isEmailRevealed;
  };

  @computed('selectedFlow.{type,employeeCentric}')
  get isFromEmployeeScreening(): boolean {
    if (!this.selectedFlow) {
      return false;
    }
    return Boolean(
      get(this.selectedFlow, 'employeeCentric') || get(this.selectedFlow, 'type') === 'Flows::EmployeeScreening',
    );
  }

  @action
  setActiveTab(tab: 'visit-details' | 'documents'): void {
    this.activeTab = tab;
  }

  @action
  toggleDocumentsSection(): void {
    this.activeTab = this.activeTab === 'documents' ? 'visit-details' : 'documents';
  }

  @action
  reviewDocuments(): void {
    this.activeTab = 'documents';
    // Below `lg` the section is not tab-gated, so switching tab alone is invisible: scroll to it.
    schedule('afterRender', () => {
      document.getElementById('documents-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  @cached
  get customUserDocumentTemplateConfigurations(): UserDocumentTemplateConfigurationModel[] {
    // selectedFlow can return Partial<FlowModel> (name-only stub, no class
    // instance), EmployeeScreeningFlowModel (sibling class, no documentUploadPage
    // relation), or the real FlowModel. instanceof narrows to the last case
    // only — one check covers all three bad cases and eliminates the cast.
    const flow = this.selectedFlow;
    if (!(flow instanceof FlowModel)) return [];
    const page = flow.belongsTo('documentUploadPage').value() as DocumentUploadPageModel | null;
    if (!page?.enabled) return [];
    return page.userDocumentTemplateConfigurations.slice().filter((c) => c.active);
  }

  get customVisitorDocuments(): VisitorDocumentModel[] {
    const templateIds = new Set((this.args.requiredDocumentTemplates ?? []).map((t) => t.id));
    return this.allVisitorDocuments.filter((doc) => templateIds.has(doc.belongsTo('userDocumentTemplate').id()));
  }

  get showDocumentsTab(): boolean {
    if (!this.abilities.can('access document-upload')) return false;
    return (this.args.requiredDocumentTemplates?.length ?? 0) > 0 || this.customVisitorDocuments.length > 0;
  }

  get hasDocumentsPendingReview(): boolean {
    return this.customVisitorDocuments.some((doc) => doc.isReview);
  }

  get documentRequirementsSummary(): Array<{
    template: { title: string };
    isUploaded: boolean;
    statusLabel: string | null;
  }> {
    const docs = this.customVisitorDocuments;
    const templates = this.args.requiredDocumentTemplates ?? [];

    return templates.map((template) => {
      const doc = docs.find((d) => d.belongsTo('userDocumentTemplate').id() === template.id);
      let statusLabel: string | null = null;
      if (doc) {
        if (doc.isApproved) statusLabel = 'Approved';
        else if (doc.isDenied) statusLabel = 'Denied';
        else statusLabel = 'Pending review';
      }
      return {
        template,
        isUploaded: !!doc,
        statusLabel,
      };
    });
  }

  @action
  reloadDocuments(): void {
    this.args.reloadDocuments?.();
  }

  @tracked adminPreRegTemplates: CustomUserDocumentTemplateModel[] = [];
  @tracked draftDocumentIds: Record<string, string> = {};
  // Template ids whose card currently has an upload in flight (the pre-confirm window).
  // Submit is disabled while non-empty so an invite can't be created before the draft id
  // is collected, which would silently drop the document.
  @tracked private uploadingTemplateIds = new Set<string>();
  // Template ids with a field-values PATCH in flight (~200ms). These do NOT disable
  // Submit — saveTask waits them out instead, so the click that committed an edit can
  // also submit (the same-gesture blur->save->click path).
  @tracked private savingTemplateIds = new Set<string>();
  // Template ids whose card reported an incomplete expiration row; Submit is disabled while
  // non-empty so the convert can't silently drop the date. Cleared like the upload set.
  @tracked private incompleteTemplateIds = new Set<string>();
  // Submits parked on the save queue; resolved push-style when `savingTemplateIds`
  // empties (a tracked getter is not observable, so a poll/observer wait can't work).
  private pendingSaveDrainWaiters: Array<() => void> = [];

  get isRecurringSelected(): boolean {
    return Boolean(this.args.inviteChangeset?.recurringRule);
  }

  /**
   * The admin pre-reg Documents section renders only when creating a fresh
   * invite, the admin may review pre-reg docs, the feature is on, and the
   * invite isn't recurring (recurring invites don't support pre-reg docs).
   */
  get canShowAdminPreRegSection(): boolean {
    return (
      Boolean(this.args.invite?.isNew) &&
      !this.args.isEditInvite &&
      !this.isRecurringSelected &&
      this.abilities.can('manage admin-pre-reg-document') &&
      this.abilities.can('review visitor-document')
    );
  }

  /** At least one attachment-capable visitor document (e.g. COVID/vaccination) is present. */
  get hasAttachmentVisitorDocuments(): boolean {
    return this.visitorDocuments.some((doc) => doc.isCapableOfAttachments);
  }

  /** The admin pre-reg required-document section is showing (gate passes + at least one template). */
  get hasAdminPreRegDocuments(): boolean {
    return this.canShowAdminPreRegSection && this.adminPreRegTemplates.length > 0;
  }

  /**
   * The COVID/attachment documents render in their own standalone "Documents" panel ONLY when there
   * is no admin pre-reg section to fold them into. When both exist they share a single "Documents"
   * header (required docs first, then attachments) so the create form never shows two "Documents".
   */
  get showStandaloneVisitorDocuments(): boolean {
    return this.hasAttachmentVisitorDocuments && !this.hasAdminPreRegDocuments;
  }

  loadAdminPreRegTemplates = restartableTask(async (flowId: string) => {
    const flow = this.selectedFlow;
    if (!(flow instanceof FlowModel)) {
      this.adminPreRegTemplates = [];
      this.loadedTemplatesFlowId = flowId;
      return;
    }
    try {
      // ED reference `.load()` is typed `any` by @typescript-eslint's service
      // (glint sees the real type), so these casts narrow off `any`, not noise.
      const page = (await flow.belongsTo('documentUploadPage').load()) as DocumentUploadPageModel | null;
      if (this.isDestroying || this.isDestroyed) return;
      // The selected flow may change during the awaits; only commit results for
      // the flow we actually loaded (a newer load handles the current flow).
      if (this.selectedFlowId !== flowId) return;
      if (!page?.enabled) {
        this.adminPreRegTemplates = [];
        this.loadedTemplatesFlowId = flowId;
        return;
      }
      // The real API serializes this relationship links-only, and Ember Data's
      // link-follow on a SYNC hasMany pushes the records without updating the
      // parent's linkage — the section silently renders empty (or stale, from a
      // previously cached linkage). Mirage masks this by inlining `data`.
      // `include` makes the backend emit the linkage and `reload` defeats the
      // stale cache, so the sync relationship populates deterministically
      // (verified end-to-end against staging).
      const reloadedPage = await this.store.findRecord('document-upload-page', page.id, {
        reload: true,
        include: 'custom-user-document-templates',
      });
      if (this.isDestroying || this.isDestroyed) return;
      if (this.selectedFlowId !== flowId) return;
      this.adminPreRegTemplates = reloadedPage.customUserDocumentTemplates.slice();
      this.loadedTemplatesFlowId = flowId;
    } catch (e) {
      if (didCancel(e)) return;
      // Optional section: render nothing on a failed fetch rather than block the
      // invite form, but surface the dropped rejection in dev/CI. RESET
      // `loadedTemplatesFlowId` so the next trigger for ANY flow retries: after
      // A-success -> B-failure the marker would otherwise still read A, and
      // switching back to A would dedup-skip the reload, leaving the section
      // hidden with templates wiped (A->B(fail)->A sticky-empty).
      this.loadedTemplatesFlowId = undefined;
      this.adminPreRegTemplates = [];
      if (config.environment !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[admin-prereg] failed to load document templates', e);
      }
    }
  });

  // Returns `string | null` (not `undefined`) to match the `@flowId?: string | null`
  // arg the document-review list/card declare — so the create-mode binding can't pass
  // `undefined` where `null` is expected. The card's guard treats either falsy value the
  // same (it never reaches confirm without a flow id), so this is a type-alignment only.
  get selectedFlowId(): string | null {
    return this.selectedFlow ? (get(this.selectedFlow, 'id') as string) : null;
  }

  // The flow whose templates are CURRENTLY committed to `adminPreRegTemplates`.
  // Set only on a successful load (never before `perform`): dedupes the
  // `did-update` re-fire on post-upload re-renders (the VIS-16887 S2 over-clear
  // regression — a same-flow re-render no-ops); lets a failed/abandoned load
  // retry (the id never advances on failure); and lets `draftDocumentsForSave`
  // detect the async flow-switch window where `adminPreRegTemplates` still holds
  // the previous flow's set.
  @tracked private loadedTemplatesFlowId: string | undefined;

  // Last (canShow, flowId) the reset ran for; plain (non-tracked) so the changeset reads below can't entangle it.
  #lastResetKey: string | null = null;

  // (Re)loads the selected flow's templates and resets per-flow tracking when the flow or
  // section visibility changes. Consumed by a template sink; returns '' so the sink renders nothing.
  @use adminPreRegAutoloader = resource<string>(() => {
    const flowId = this.selectedFlowId;
    const canShow = this.canShowAdminPreRegSection;
    void this.syncAdminPreRegTemplatesTask.perform(flowId, canShow);
    return '';
  });

  syncAdminPreRegTemplatesTask = restartableTask(async (flowId: string | null, canShow: boolean) => {
    // Defer past render so the reset can't mutate tracked state consumed during render.
    await Promise.resolve();
    if (this.isDestroying || this.isDestroyed) return;
    // Reset only on a real (canShow, flowId) transition: the resource re-runs on every changeset
    // write (deps share one ember-changeset tag), so an unconditional reset would wipe a live draft.
    const resetKey = `${canShow}:${flowId ?? ''}`;
    if (resetKey !== this.#lastResetKey) {
      this.#lastResetKey = resetKey;
      this.resetAdminPreRegTracking();
    }
    if (!canShow || !flowId) return;
    // Already-loaded flow: skip the reload (no flash). Failed loads retry (id only advances on success).
    if (flowId === this.loadedTemplatesFlowId) return;
    await this.loadAdminPreRegTemplates.perform(flowId);
  });

  // Drop draft ids and in-flight tracking a torn-down card left behind, so a remounted empty
  // card can't re-attach a draft or leave Submit stuck.
  resetAdminPreRegTracking(): void {
    if (Object.keys(this.draftDocumentIds).length > 0) this.draftDocumentIds = {};
    if (this.uploadingTemplateIds.size > 0) this.uploadingTemplateIds = new Set();
    if (this.savingTemplateIds.size > 0) this.savingTemplateIds = new Set();
    if (this.incompleteTemplateIds.size > 0) this.incompleteTemplateIds = new Set();
  }

  /**
   * Fires as soon as a draft exists (status `processing`) so parsing never
   * blocks submit; fires with `null` on remove/replace to retract the id.
   */
  @action
  collectDraftDocument(templateId: string, draftId: string | null): void {
    const next = { ...this.draftDocumentIds };
    if (draftId) next[templateId] = draftId;
    else delete next[templateId];
    this.draftDocumentIds = next;
  }

  /**
   * A card reports it has started (true) or finished (false) uploading a file. While any
   * card is mid-upload — the pre-confirm window where no draft id has been collected yet —
   * Submit is disabled so the invite can't be created with the document silently missing.
   * Parsing is NOT reported as in-flight: the draft id is already collected by then.
   */
  @action
  handleDocumentUploadState(templateId: string, isUploading: boolean, kind: DocumentBusyKind = 'upload'): void {
    // 'incomplete' hard-blocks submit like 'upload' (not drain-and-wait like 'field-values-save').
    if (kind === 'incomplete') {
      const next = new Set(this.incompleteTemplateIds);
      if (isUploading) next.add(templateId);
      else next.delete(templateId);
      this.incompleteTemplateIds = next;
      return;
    }
    const source = kind === 'upload' ? this.uploadingTemplateIds : this.savingTemplateIds;
    const next = new Set(source);
    if (isUploading) next.add(templateId);
    else next.delete(templateId);
    if (kind === 'upload') this.uploadingTemplateIds = next;
    else {
      this.savingTemplateIds = next;
      if (next.size === 0 && this.pendingSaveDrainWaiters.length > 0) {
        const waiters = this.pendingSaveDrainWaiters;
        this.pendingSaveDrainWaiters = [];
        waiters.forEach((resolve) => resolve());
      }
    }
  }

  // The `canShowAdminPreRegSection` guard here is load-bearing, not just an optimization:
  // a card unmounting on an isEditInvite / ability-lost transition has no Set reset, so
  // this clause is what keeps a stale entry from leaving Submit disabled. Do not drop it.
  get hasInFlightDocumentUploads(): boolean {
    return this.canShowAdminPreRegSection && this.uploadingTemplateIds.size > 0;
  }

  get hasPendingDocumentSaves(): boolean {
    return this.canShowAdminPreRegSection && this.savingTemplateIds.size > 0;
  }

  // Disable Submit while any card reports an incomplete row. Same canShowAdminPreRegSection
  // backstop as the upload/save gates (an unmounting card has no Set reset of its own).
  get hasIncompleteDocuments(): boolean {
    return this.canShowAdminPreRegSection && this.incompleteTemplateIds.size > 0;
  }

  get draftDocumentsForSave(): InviteDraftDocument[] {
    // Don't attach drafts the user can no longer see (e.g. switched to recurring).
    if (!this.canShowAdminPreRegSection) return [];
    // During an async flow switch, `adminPreRegTemplates` still holds the prior
    // flow's set until the reload commits — guard on the flow that produced them
    // so a submit mid-reload can't attach the prior flow's draft to this invite.
    if (this.loadedTemplatesFlowId !== this.selectedFlowId) return [];
    // Only attach drafts for documents still required by the selected flow — a
    // flow switch leaves stale entries for templates that are no longer rendered.
    // `templateId` rides along so the serializer can emit it as relationship meta
    // (the backend convert step pairs each converted doc to its template by id).
    const currentTemplateIds = new Set(this.adminPreRegTemplates.map((t) => t.id));
    return Object.entries(this.draftDocumentIds)
      .filter(([templateId]) => currentTemplateIds.has(templateId))
      .map(([templateId, id]) => ({ id, templateId }));
  }

  /**
   * All visitor documents loaded for this invite — includes custom uploads
   * whose templates are NOT in activeUserDocumentTemplateConfigurations.
   * Cached to avoid repeated .slice() calls across multiple getters.
   */
  @cached
  get allVisitorDocuments(): VisitorDocumentModel[] {
    if (!this.args.inviteChangeset?.visitorDocumentsLoaded) return [];
    return this.args.inviteChangeset?.visitorDocuments?.slice() ?? [];
  }

  get visitorDocuments(): VisitorDocumentModel[] {
    const {
      activeUserDocumentTemplateConfigurations,
      args: { inviteChangeset },
    } = this;

    return (activeUserDocumentTemplateConfigurations?.content ?? [])
      .toSorted((a, b) => a.userDocumentTemplate.content!.position - b.userDocumentTemplate.content!.position)
      .map((config) => {
        const { userDocumentTemplate } = config;

        return (
          inviteChangeset.visitorDocumentForTemplate(
            'content' in userDocumentTemplate ? userDocumentTemplate.content! : userDocumentTemplate,
          ) || this.store.createRecord('visitor-document', { userDocumentTemplate: userDocumentTemplate.content })
        );
      });
  }

  get isValidVisitorDocuments(): boolean {
    const documentsToCheck = this.visitorDocuments?.filter((document) => document.hasAttachedFile);
    if (isEmpty(documentsToCheck)) return true;

    return documentsToCheck.every((document) => document.isValidDocument);
  }

  @computed('sortedSignInFields.[]', 'userDataChangesets')
  get fieldChangesets(): Array<DetailedChangeset<SignInFieldModel>> {
    return this.sortedSignInFields.map((field) => {
      return get(this.userDataChangesets, `field-${field.id}`) || this.buildFieldChangeset(field);
    });
  }

  @computed('fieldChangesets.@each.value', 'selectedFlow')
  get renderableFieldChangesets(): DetailedChangeset<SignInFieldModel>[] {
    const renderableFields = this.fieldChangesets.filter((fieldChangeset) => {
      if (get(fieldChangeset, 'isTopLevel')) {
        return true;
      }

      const action = fieldChangeset.actionableSignInFieldActions?.content[0];

      // Do not attempt to automatically async fetch related signInField
      // If signInField record isn't already fulfilled, it will probably not load
      const isApplicableAction = action && action.belongsTo('signInField').value() && get(action, 'signInField.id');

      if (!isApplicableAction) {
        return false;
      }

      const changesetParent = this.fieldChangesets.find((changeset) => changeset.id === get(action, 'signInField.id'));

      return changesetParent && get(changesetParent, 'value') === get(action, 'dropdownOption.value');
    });

    if (renderableFields.length === 0) {
      const flowId = <string>(
        (this.selectedFlow
          ? get(this.selectedFlow, 'id') || get(this.selectedFlow, 'content.id') || 'unknown'
          : 'unknown')
      );

      this.metrics.trackEvent('Sign-in Fields Issue', {
        issue: 'No fields rendered',
        flowId,
      });
    }

    return renderableFields;
  }

  @computed('args.inviteChangeset.{validationMap.email,email}')
  get canSendGuestEmail(): boolean {
    const email = this.args.inviteChangeset.email;
    const validation = <[(...args: unknown[]) => boolean]>this.args.inviteChangeset.validationMap?.['email'];

    const validate = Array.isArray(validation)
      ? (...args: unknown[]) => {
          return validation.every((v) => v(...args) === true);
        }
      : () => true;

    return Boolean(email && validate('email', email) === true);
  }

  @computed('args.inviteChangeset.userData.[]')
  get phoneNumber(): UserDatum | undefined {
    const userData = this.args.inviteChangeset.userData || A();
    return userData.findBy('field', 'Your Phone Number');
  }

  @computed('args.inviteChangeset.isNew', 'timezone')
  get minArrivalDate(): moment.Moment | string {
    const timezone = get(this, 'timezone');

    if (<boolean>(<unknown>this.args.inviteChangeset.isNew)) {
      return timezone ? moment().tz(timezone) : moment();
    }

    return '';
  }

  @computed('args.inviteChangeset.isNew', 'expectedArrivalTime', 'timezone')
  get minArrivalTime(): string {
    const timezone = this.timezone;
    const now = timezone ? moment().tz(timezone).format('h:mm a') : moment().format('h:mm a');

    if (!this.expectedArrivalTime) return now;

    const isNew = get(this, 'args.inviteChangeset.isNew');
    const isForToday = moment(this.expectedArrivalTime).isSame(moment(), 'day');

    return isNew && isForToday ? now : '';
  }

  @computed('expectedArrivalTime', 'timezone')
  get selectedTime(): string {
    if (!this.expectedArrivalTime) return '';

    const timezone = this.timezone;
    const time = timezone ? moment(this.expectedArrivalTime).tz(timezone) : moment(this.expectedArrivalTime);
    return time.format('h:mm a');
  }

  // we only care if the model changes since the POV field can't be deleted
  // this is not a Flow model but rather the user-data containing the flow-name
  @computed('args.inviteChangeset.{userData.@each.field,flowName}')
  get purposeOfVisit(): UserDatum | Partial<UserDatum> {
    const userData = this.args.inviteChangeset.userData || A();
    const flowName = this.args.inviteChangeset.flowName;

    return userData.findBy('field', 'Purpose of visit') || { field: 'Purpose of visit', value: flowName };
  }

  @computed('args.{invite.userData,inviteChangeset.location.id}', 'selectedFlow', 'signInFields')
  get userDataChangesets(): Record<string, DetailedChangeset<SignInFieldModel>> {
    const initialState = <Record<string, DetailedChangeset<SignInFieldModel>>>EmberObject.create({});

    // bootstrap the initial state for the changeset so it uses whatever is in the invite.userData
    // this is required to show pre-existing data
    const userData = this.args.invite.userData || A();
    userData.forEach((option, index) => {
      const fieldName = get(option, 'field');
      const value = get(option, 'value');
      // ignore purpose of visit here
      // eslint-disable-next-line ember/use-ember-get-and-set
      if (value !== get(this.selectedFlow ?? {}, 'name') && fieldName) {
        let field = this.signInFields?.find((f) => f.name === fieldName);

        if (!field) {
          field = {
            id: `${index}-${<string>get(this, 'args.inviteChangeset.location.id')}`, // pseudo-id
            name: fieldName,
            // @ts-ignore
            isLoaded: true,
          };
        }

        const fieldChangeset = this.buildFieldChangeset(field);
        set(fieldChangeset, 'value', value); // set the initial value

        set(initialState, this.pseudoIdForField(fieldChangeset), fieldChangeset);
      }
    });
    return initialState;
  }

  get shouldShowGroupName(): boolean {
    if (!this.featureFlags.isEnabled('visitors-group-invite-updates-m1')) return false;
    if (!this.state.features?.canAccessGroupInvitesFeature) return false;

    return this.args.invite?.isInGroupInvite;
  }

  isVisitorDocumentPersistable(visitorDocument: VisitorDocumentModel): boolean {
    const activeIdentifiers = (this.activeUserDocumentTemplateConfigurations?.content ?? [])
      .map((config) => config.userDocumentTemplate.content?.identifier)
      .filter(Boolean);
    const isAssociatedToActiveTemplate = activeIdentifiers.includes(visitorDocument.identifier);
    const hasAttachmentsWithPendingUploads = visitorDocument.userDocumentAttachmentsPendingUpload.length > 0;

    return isAssociatedToActiveTemplate && hasAttachmentsWithPendingUploads;
  }

  // prettier-ignore
  @(employeesSearcherTask({
    filter: { deleted: false, excludeHidden: true },
    prefix: false,
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  @action
  saveAndAddAnother(): void {
    // No in-flight guard here: saveTask waits for a queued field-values PATCH to drain
    // (bounded grace) so the click that committed an edit also submits — see saveTask.
    void this.saveTask.perform({ addAnother: true });
  }

  @action
  async onFormSubmit(e: Event): Promise<void> {
    e.preventDefault();
    // No in-flight guard here (covers Enter-key submits too): saveTask waits for a queued
    // field-values PATCH to drain (bounded grace) and still bails on a genuine upload, so
    // the draft id can never be missed — see saveTask.
    if (this.secondaryLocationNames.length && this.args.isEditInvite) {
      this.toggleMultiLocationsEditModal();
    }

    if (this.editAffectsMultipleLocationsModal) {
      return;
    }
    if (this.hasRecurringInvite) {
      // Under the split flag, resolve the policy draft before opening the picker so the folded
      // re-eval warning's re-run gate (shouldShowRecurringReevaluationWarning) is accurate at render.
      if (this.recurringInviteOneOffSplitEnabled && this.shouldShowWorkflowReevaluationModal) {
        await this.shouldOpenReevaluationModal();
        if (this.isDestroying || this.isDestroyed) return;
      }
      this.isShowingEditRecurringInvite = true;
      return;
    }
    if (this.shouldShowWorkflowReevaluationModal) {
      const shouldOpen = await this.shouldOpenReevaluationModal();
      if (shouldOpen) {
        this.isShowingWorkflowReevaluationModal = true;
        return;
      }
    }
    if (this.shouldSendNotification) {
      this.sendNotificationCallback = () => this.saveTask.perform();
    } else {
      void this.saveTask.perform();
    }
  }

  @action
  onWorkflowReevaluationClose(): void {
    this.isShowingWorkflowReevaluationModal = false;
  }

  @action
  onWorkflowReevaluationConfirm(reason: string | null): void {
    this.isShowingWorkflowReevaluationModal = false;
    this.workflowReevaluationReason = reason;
    if (this.shouldSendNotification) {
      this.sendNotificationCallback = () => this.saveTask.perform();
    } else {
      void this.saveTask.perform();
    }
  }

  @action
  onSubmitRecurringInvite(whichInvites: SaveOptions['whichInvites'], reason?: string | null): TaskInstance<void> {
    return this.saveTask.perform({ whichInvites, workflowReevaluationReason: reason });
  }

  @action
  buildFieldChangeset(field: SignInFieldModel | undefined): DetailedChangeset<SignInFieldModel> {
    const validations = buildFlowFieldValidations(field);
    const validator = lookupValidator(validations);
    return Changeset(field, validator, validations);
  }

  @action
  async updatePropsRecurringInvite(recurringInvite: RecurringInvite): Promise<RecurringInvite> {
    const inviteChangeset = this.args.inviteChangeset;
    const newExpectedArrivalTime = inviteChangeset.expectedArrivalTime;

    setProperties(recurringInvite, {
      email: inviteChangeset.email,
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      employee: inviteChangeset.employee?.content ?? inviteChangeset.employee,
      fullName: inviteChangeset.fullName,
      location: inviteChangeset.location?.content,
      privateNotes: inviteChangeset.privateNotes,
      visitorDocuments: inviteChangeset.visitorDocuments,
      childLocations: await inviteChangeset.childInviteLocations,
    });

    if (newExpectedArrivalTime) {
      const existingStartTime = moment(recurringInvite.startTime);
      const newHour = moment(newExpectedArrivalTime).hour();
      const newMinute = moment(newExpectedArrivalTime).minute();
      const newStartTime = moment(existingStartTime).hour(newHour).minute(newMinute).toDate();

      setProperties(recurringInvite, {
        startTime: newStartTime,
      });
    }

    if (inviteChangeset.recurringRule) {
      recurringInvite.recurringRule = inviteChangeset.recurringRule;
    }

    return recurringInvite;
  }

  @action
  modifySecondaryLocations(locations?: LocationModel[]): void {
    this.args.inviteChangeset.childInviteLocations = locations;
  }

  @action
  modifySecondaryLocationOptions(options: SecondaryLocationOption[]): void {
    // Pass through already-selected locations even if their eligibility
    // flipped after selection; only require eligibility for new additions.
    // Filtering the full list silently drops chips on unrelated edits
    // (Backspace one chip, lose two).
    const currentLocations = (this.args.inviteChangeset.childInviteLocations ?? []) as LocationModel[];
    const currentIds = new Set(currentLocations.map((loc) => loc.id));
    const accepted = options
      .filter((opt) => currentIds.has(opt.location.id) || opt.eligible)
      .map((opt) => opt.location);
    this.modifySecondaryLocations(accepted);
  }

  // Shared field map for a new RecurringInvite/one-off. Pure: the caller adds (or omits) recurringRule.
  #buildRecurringRecordAttrs(): Record<string, unknown> {
    const inviteChangeset = this.args.inviteChangeset;
    return {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      employee: inviteChangeset.employee?.content ?? inviteChangeset.employee,
      location: inviteChangeset.location?.content,
      fullName: inviteChangeset.fullName,
      email: inviteChangeset.email,
      privateNotes: inviteChangeset.privateNotes,
      skipGuestNotification: !this.sendGuestEmail && this.canSendGuestEmail,
      visitorDocuments: inviteChangeset.visitorDocuments,
      propertyNotes: inviteChangeset.propertyNotes,
      locality: inviteChangeset.locality,
    };
  }

  #finalizeAndSaveRecurringRecord(
    record: RecurringInvite,
    adapterOptions?: InviteSaveAdapterOptions,
  ): Promise<RecurringInvite> {
    const inviteChangeset = this.args.inviteChangeset;
    // make additional guests always 0 if selected flow doesn't support it
    record.additionalGuests = get(this, 'selectedFlow.additionalGuests') ? inviteChangeset.additionalGuests : 0;
    record.userData = inviteChangeset.userData;
    // @ts-ignore
    record.flow = this.selectedFlow;
    inviteChangeset.rollback();
    return record.save(adapterOptions ? { adapterOptions } : undefined);
  }

  saveRecurringInviteTask = dropTask(
    async (
      existingRecurringInvite: RecurringInvite,
      whichInvites: SaveOptions['whichInvites'],
      adapterOptions?: InviteSaveAdapterOptions,
    ) => {
      const inviteChangeset = this.args.inviteChangeset;
      let recurringInvite: RecurringInvite;

      if (existingRecurringInvite && existingRecurringInvite.id && whichInvites === RECURRING_OPTIONS.ALL) {
        recurringInvite = await this.updatePropsRecurringInvite(existingRecurringInvite);
      } else {
        recurringInvite = this.store.createRecord('recurring-invite', {
          ...this.#buildRecurringRecordAttrs(),
          recurringRule: inviteChangeset.recurringRule,
        });

        recurringInvite.childLocations = await inviteChangeset.childInviteLocations;
        recurringInvite.startTime = this.expectedArrivalTime;

        if (whichInvites === RECURRING_OPTIONS.THIS_AND_FOLLOWING) {
          recurringInvite.parentRecurringInvite = existingRecurringInvite;

          if (!recurringInvite.recurringRule) {
            recurringInvite.recurringRule = existingRecurringInvite.recurringRule;
          }
        }
      }

      return this.#finalizeAndSaveRecurringRecord(recurringInvite, adapterOptions);
    },
  );

  // Splits "This" occurrence via the dedicated one-off endpoint: previousRecurringInvite carries the
  // series, the API derives the single-occurrence rule from splitOccurrenceDate + the edited time.
  saveOneOffSplitTask = dropTask(async (adapterOptions?: InviteSaveAdapterOptions) => {
    const inviteChangeset = this.args.inviteChangeset;
    const previousRecurringInvite = await this.args.invite.recurringInvite;
    // Invariant (picker only opens for recurring invites): make the null case explicit rather
    // than posting a one-off with no source series.
    if (!previousRecurringInvite) {
      throw new Error('Cannot split a single occurrence: the invite has no recurring invite');
    }
    const oneOff = this.store.createRecord('recurring-invite-one-off', {
      ...this.#buildRecurringRecordAttrs(),
      previousRecurringInvite,
      splitOccurrenceDate: this.splitOccurrenceDate,
    });

    oneOff.childLocations = await inviteChangeset.childInviteLocations;
    oneOff.startTime = this.expectedArrivalTime;

    return this.#finalizeAndSaveRecurringRecord(oneOff, adapterOptions);
  });

  saveTask = dropTask(async (options: SaveOptions = {}) => {
    // Backstop for every save entry point (onFormSubmit, saveAndAddAnother, workflow
    // reevaluation, recurring): never persist while a pre-reg document upload or a
    // field-values save is still in flight — the convert reads field-values server-side,
    // so a submit that overtakes the PATCH would auto-approve a document with stale dates.
    // The common in-flight case is the same-gesture race (the click that blurs a date-name
    // input also submits, ~200ms PATCH), so wait briefly for the queue to drain and proceed —
    // a single click then Just Works instead of being silently swallowed. A drain longer than
    // the grace window (a real file upload) keeps the previous behavior: bail out, the
    // disabled button + live-region hint explain why. rawTimeout (not timeout) so test
    // waiters don't deadlock the settled state while a test intentionally holds the PATCH.
    if (this.hasInFlightDocumentUploads) return;
    // Keyboard/Enter submit bypasses the disabled button, so re-check incompleteness here and flash.
    if (this.hasIncompleteDocuments) {
      this.flashMessages.showFlash(
        'error',
        'Add a name and date for each expiration entry, or remove the incomplete row.',
      );
      return;
    }
    if (this.hasPendingDocumentSaves) {
      const waiterToken = testWaiter.beginAsync();
      try {
        await Promise.race([
          new Promise<void>((resolve) => this.pendingSaveDrainWaiters.push(resolve)),
          rawTimeout(DOCUMENT_SAVE_DRAIN_GRACE_MS),
        ]);
      } finally {
        testWaiter.endAsync(waiterToken);
      }
      if (this.hasPendingDocumentSaves) return;
    }
    const defaultOptions = { addAnother: false };

    options = Object.assign({}, defaultOptions, options);

    const inviteChangeset = this.args.inviteChangeset;
    await inviteChangeset.validate();

    // Only validate RENDERED / VISIBLE field changesets
    for (const index of this.renderableFieldChangesets.keys()) {
      const changeset = this.renderableFieldChangesets[index];
      await changeset?.validate();
    }

    if (this.selectedFlow && !get(this.selectedFlow, 'id')) {
      this.flashMessages.showFlash('error', 'This sign-in flow no longer exists. Please select another one.');
      return;
    }

    // Block save when the selected flow became ineligible (e.g., primary
    // switched mid-form to a location lacking global_sign_in_flows). The
    // chip renders disabled but the existing value still satisfies isInvalid.
    if (this.selectedFlowOption && !this.selectedFlowOption.eligible) {
      this.flashMessages.showFlash(
        'error',
        "This visitor type isn't available on this location's plan. Pick another type or upgrade the location.",
      );
      return;
    }

    if (inviteChangeset.isInvalid) {
      const specificError = <string>get(inviteChangeset, 'errors.firstObject.validation');
      this.flashMessages.showFlash('error', 'Please fill all the required fields.', specificError);
      return;
    }

    for (const index of this.renderableFieldChangesets.keys()) {
      const changeset = this.renderableFieldChangesets[index];
      if (get(changeset, 'isInvalid')) {
        const specificError = <string>get(changeset, 'errors.firstObject.validation.firstObject');
        if (specificError === 'Please enter a valid date') {
          this.flashMessages.showFlash('error', 'Please enter a valid date');
        } else {
          this.flashMessages.showFlash('error', 'Please fill all the required fields.', specificError);
        }
        return;
      }
    }

    if (!this.isValidVisitorDocuments) {
      this.flashMessages.showFlash('error', 'Please enter all required information');
      return;
    }

    // If invite has additional hosts added *AND* has multiple locations or is recurring, show an error and don't save.
    // This behavior will be improved in the future; this solution is a stop-gap measure to simply prevent recurring
    // or multi-location invites from being created at all.
    if (<number>inviteChangeset.additionalHosts?.length > 0) {
      const isRecurring = !!inviteChangeset.recurringRule;
      const hasMultipleLocations = <number>inviteChangeset.childInviteLocations?.length > 0;
      let error;
      if (isRecurring && hasMultipleLocations) {
        error =
          'Recurring invites and multiple locations are not supported for invites with multiple hosts. Please either remove the additional hosts, or make the invite a one-time invite for a single location.';
      } else if (isRecurring) {
        error =
          'Recurring invites cannot be created with multiple hosts. Please remove additional hosts or create a one-time invite.';
      } else if (hasMultipleLocations) {
        error = 'Please remove either secondary locations or additional hosts to save this invite.';
      }
      if (error) {
        this.flashMessages.showFlash('error', 'Unable to invite / save', error);
        return;
      }
    }

    set(inviteChangeset, 'skipGuestNotification', !this.sendGuestEmail && this.canSendGuestEmail);

    const userDataChangesets = this.userDataChangesets;

    Object.values(userDataChangesets).forEach((fieldChangeset) => {
      if (get(fieldChangeset, 'isDirty')) {
        addOrUpdateUserData(inviteChangeset, fieldChangeset.name, fieldChangeset.value);
      }
    });

    if (!get(this, 'selectedFlow.additionalGuests')) {
      // make additional guests always 0 if selected flow doesn't
      // support it
      set(inviteChangeset, 'additionalGuests', 0);
    }

    const token = isTesting ? testWaiter.beginAsync() : false;

    try {
      const date = this.expectedArrivalTime;
      const recurringInvite = await this.args.invite.recurringInvite;
      const recurringInviteId = recurringInvite ? recurringInvite.id : null;
      const whichInvites = options.whichInvites;
      const visitorDocuments = <VisitorDocumentModel[]>(<unknown>inviteChangeset?.visitorDocuments) ?? A();
      // Creates a new RecurringInvite for: a new recurring rule, this-and-following/all, or a
      // single-occurrence "This" split under recurring_invite_one_off_split.
      const isSavingRecurringInvite =
        get(inviteChangeset, 'recurringRule') ||
        (recurringInviteId &&
          (whichInvites === RECURRING_OPTIONS.THIS_AND_FOLLOWING ||
            whichInvites === RECURRING_OPTIONS.ALL ||
            this.#isOneOffSplit(whichInvites)));

      let saveError;

      if (isSavingRecurringInvite) {
        const inviteAttrs = getProperties(
          inviteChangeset,
          'flowName',
          'expectedArrivalTime',
          'employee',
          'inviterName',
        );

        const recurringAdapterOptions: InviteSaveAdapterOptions = {};
        if (options.workflowReevaluationReason !== undefined) {
          recurringAdapterOptions.workflowReevaluationReason = options.workflowReevaluationReason;
        }
        const adapterOptions = Object.keys(recurringAdapterOptions).length ? recurringAdapterOptions : undefined;
        const savedRecurringInvite = this.#isOneOffSplit(whichInvites)
          ? await this.saveOneOffSplitTask.perform(adapterOptions)
          : await this.saveRecurringInviteTask.perform(recurringInvite, whichInvites, adapterOptions);

        try {
          await this.saveVisitorDocumentsTask.perform(savedRecurringInvite, visitorDocuments);
        } catch (_) {
          // Recurring invites create "child" invites async on the backend.
          // If a recurring invite saves but its documents do not, there is not
          // much we can do to position the user to retry the upload. For now,
          // proceeding with existing logic with a flash message to report that
          // something went wrong. ~ dana mar.3.2022
          saveError = 'Recurring invite saved! But one or more document uploads failed';
        }

        if (options.addAnother) {
          this.metrics.trackEvent('Dashboard Invite - Add Another', {
            recurring_invite_id: savedRecurringInvite.id,
          });
        }

        const goToIndex = Boolean(saveError) || !options.addAnother;

        this.args.modelDidSave(date, goToIndex, inviteAttrs);
      } else {
        const adapterOptions: InviteSaveAdapterOptions = {};
        if (this.workflowReevaluationReason !== undefined) {
          adapterOptions.workflowReevaluationReason = this.workflowReevaluationReason;
        }
        const draftDocuments = this.draftDocumentsForSave;
        if (draftDocuments.length) {
          adapterOptions.draftDocuments = draftDocuments;
        }
        const saveOptions = Object.keys(adapterOptions).length ? { adapterOptions } : {};
        const invite = await inviteChangeset.save(saveOptions);

        try {
          await this.saveVisitorDocumentsTask.perform(invite, visitorDocuments);
        } catch (error) {
          this.flashMessages.showFlash('error', 'Invite saved! But one or more document uploads failed');

          // Handle invite successfully persisted but associated visitor document upload/save failed
          return this.args.modelDidPartialSave?.(invite, error);
        }

        if (options.addAnother) {
          this.metrics.trackEvent('Dashboard Invite - Add Another', { invite_id: invite.id });
        }

        this.args.modelDidSave(date, !options.addAnother, invite, { addAnother: options.addAnother });
      }

      this.workflowReevaluationReason = undefined;
      // Drafts are consumed by this save; don't carry them into the next invite
      // (e.g. "Invite and add another" reuses this component instance). Clear any
      // in-flight upload tracking too. The cards themselves reset on add-another via the
      // saved invite's isNew->false flip hiding the section (canShowAdminPreRegSection).
      this.draftDocumentIds = {};
      this.uploadingTemplateIds = new Set();
      this.savingTemplateIds = new Set();
      this.incompleteTemplateIds = new Set();

      // Clean changeset values
      Object.values(userDataChangesets).forEach((fieldChangeset) => {
        set(fieldChangeset, 'value', '');
      });

      if (saveError) {
        this.flashMessages.showFlash('error', saveError);
      } else {
        this.flashMessages.showAndHideFlash('success', 'Saved!');
      }
    } catch (e) {
      this.metrics.trackEvent('Error creating invite', {
        invite: inviteChangeset,
      });

      this.flashMessages.showFlash('error', parseErrorForDisplay(e));

      // NOTE we want to throw the error so rejection can be handled downstream as well
      throw e;
    } finally {
      if (token) testWaiter.endAsync(token);
    }
  });

  @action
  setHost(employee: EmployeeModel): void {
    const inviteChangeset = this.args.inviteChangeset;
    inviteChangeset.inviterName = employee?.name;
    inviteChangeset.employee = <AsyncBelongsTo<EmployeeModel>>(<unknown>employee);
  }

  deleteRecurringInviteTask = dropTask(async (existingRecurringInvite: RecurringInvite) => {
    // this will be the end date for deleting this and following events
    // it's technically still a modify event, so PATCH request to v3/recurring-invites
    // the end date will be today, local time
    const date = moment(this.expectedArrivalTime).format('YYYY-MM-DD');
    const dayBeforeDate = moment(this.expectedArrivalTime).subtract(1, 'day').toDate();

    // get the existing recurrence rule and change it to be exactly as it is with a new until date
    // use recurrenceRule
    const parsedRule = RecurringRule.parse(existingRecurringInvite.recurringRule);
    parsedRule.until = dayBeforeDate;
    const newRRule = parsedRule.toString();

    setProperties(existingRecurringInvite, {
      recurringRule: newRRule,
    });
    await existingRecurringInvite.save();
    this.metrics.trackEvent('Recurring Invite Delete Requested', {
      recurring_invite_id: existingRecurringInvite.id,
      invite_id: get(this.args.inviteChangeset, 'id'),
    });
    void this.router.transitionTo('visitors.invites', {
      queryParams: { date },
    });
  });

  deleteTask = dropTask(async (options: { thisAndFollowing?: boolean } = {}) => {
    try {
      const recurringInvite = await this.args.invite.recurringInvite;
      const recurringInviteId = recurringInvite ? recurringInvite.id : null;
      const isDeletingRecurringInvite = recurringInviteId && options.thisAndFollowing;

      // check if this is part of a recurring invite
      if (isDeletingRecurringInvite) {
        void this.deleteRecurringInviteTask.perform(recurringInvite);
      } else {
        await this.args.invite.destroyRecord();

        this.flashMessages.showAndHideFlash('success', 'Deleted');
        this.args.modelDidSave();
      }
    } catch (_e) {
      this.flashMessages.showFlash('error', 'Invite cannot be deleted');
    }
  });

  @action
  onDeleteInvite(whichInvite: ValueOf<typeof RECURRING_OPTIONS>): TaskInstance<void> {
    const thisAndFollowing = whichInvite === RECURRING_OPTIONS.THIS_AND_FOLLOWING;
    return this.deleteTask.perform({ thisAndFollowing });
  }

  @action
  setDate(date: Date): void {
    // this is setting a date object, possible cause of bugs at the end of the month
    set(this.args.inviteChangeset, 'expectedArrivalTime', date);
    if (this.args.dateChanged) {
      this.args.dateChanged(date);
      this.args.inviteChangeset.rruleUntil = null;
      this.args.inviteChangeset.recurringRule = null;
    }
  }

  @action
  toggleMultiLocationsEditModal(): void {
    this.editAffectsMultipleLocationsModal = !this.editAffectsMultipleLocationsModal;
  }

  @action
  toggleConfirmVisitorTypeChangeModal(): void {
    this.showConfirmVisitorTypeChangeModal = !this.showConfirmVisitorTypeChangeModal;
  }

  @action
  toggleConfirmVisitorTypeChangeModalAndClear(): void {
    this.modifySecondaryLocations();
    this.updateFlow(this.flowToChangeTo!);
    this.showConfirmVisitorTypeChangeModal = !this.showConfirmVisitorTypeChangeModal;
  }

  @action
  updateFlowOption(option: FlowOption | null | undefined): void {
    if (!option || !option.eligible) return;
    this.updateFlow(option.flow);
  }

  // Reactively corrects the selection once scoped-sub eligibility settles (replaces the
  // did-update modifier). A template `{{...}}` sink consumes it; the task defers past render
  // so the changeset writes below land in a fresh transaction. The same-tick read-then-write
  // is what tripped Glimmer's backtracking assertion (see the deferred reconcile test).
  @use eligibilityReconciler = resource<string>(() => {
    void this.scopedSubsByLocation;
    void this._primaryLocationId;
    void this.reconcileSelectedFlowAfterEligibilityTask.perform();
    return '';
  });

  reconcileSelectedFlowAfterEligibilityTask = restartableTask(async () => {
    await Promise.resolve();
    if (this.isDestroying || this.isDestroyed) return;
    this.reconcileSelectedFlowAfterEligibility();
  });

  // After a primary-location switch, scoped-sub data settles asynchronously and may flip the
  // current selection from eligible (loading-window default) to ineligible. Auto-correct to the
  // first eligible option so the picker doesn't show a disabled selection.
  @action
  reconcileSelectedFlowAfterEligibility(): void {
    if (!this.featureFlags.isEnabled(MPB_FEATURE_FLAG)) return;
    const current = this.selectedFlowOption;
    if (!current || current.eligible) return;
    const firstEligible = this.flowOptions.find((opt) => opt.eligible);
    if (firstEligible && firstEligible.flow.id !== current.flow.id) {
      // System-initiated correction. The auto-selected eligible flow is local
      // (every global flow is ineligible on a non-capable primary), and a
      // local flow can't keep secondaries, so clear them — that also drops
      // updateFlow's confirm-modal guard (childInviteLocations?.length) so the
      // flow applies directly. Surface a non-blocking flash so the removal
      // isn't silent, since this path skips the manual confirm modal.
      const hadSecondaries = Boolean(this.args.inviteChangeset.childInviteLocations?.length);
      this.modifySecondaryLocations();
      if (hadSecondaries) {
        this.flashMessages.showFlash(
          'warning',
          'Additional locations were removed because they aren’t compatible with the new visitor type.',
        );
      }
      this.updateFlow(firstEligible.flow);
    }
  }

  @action
  async handleLocationSelected(location: LocationModel): Promise<void> {
    if (!this.args.onLocationSelectedTask) return;
    try {
      await this.args.onLocationSelectedTask.perform(location);
      // Setting _primaryLocationId re-runs scopedSubsAutoloader, which performs the
      // consolidated [primary, ...siblings] fetch. A second perform here would cancel
      // that one and leave the primary missing from scopedSubsByLocation (fail-open).
      this._primaryLocationId = location?.id ?? null;
    } catch {
      // Task cancelled or rejected — keep _primaryLocationId consistent
      // with args.invite so flowOptions doesn't promise eligibility for a
      // primary that never loaded.
    }
  }

  @action
  updateFlow(flow: FlowModel): void {
    const previousFlow = this.args.inviteChangeset.flowName;

    this.metrics.trackEvent('Visitor Type Changed', {
      previousFlow,
      newFlow: flow?.name,
      flowId: flow?.id,
    });

    // Added to handle resetting fields for Multi-Location Invite option
    if (!this.showConfirmVisitorTypeChangeModal && this.args.inviteChangeset.childInviteLocations?.length) {
      this.toggleConfirmVisitorTypeChangeModal();
      this.flowToChangeTo = flow;
      return;
    }
    // TODO merge these in app level, and move the setting to the serializer
    set(this.purposeOfVisit, 'value', flow.name);
    this.args.inviteChangeset.flowName = flow.name;
    // assign flow (not sent up via PATCH request, but used by the UI. `flow-name` currently controls what flow gets assigned)
    // @ts-expect-error
    this.args.inviteChangeset.flow = flow;

    if (shouldClearRecurringForFlow(flow, this.featureFlags)) {
      this.args.inviteChangeset.recurringRule = null;
      this.args.inviteChangeset.rruleUntil = null;
    }

    this.args.onFlowChanged?.(flow.name, this.args.inviteChangeset);
  }

  @action
  pseudoIdForField(field: { id: string }): string {
    // The key provided to set must be a string.
    return `field-${field.id}`;
  }

  @action
  updateUserDataField(changeset: DetailedChangeset<SignInFieldModel>): void {
    this.userDataChangesets[this.pseudoIdForField(changeset)] = changeset;
  }

  @action
  setAdditionalHosts(hosts: EmployeeModel[]): void {
    this.args.inviteChangeset.additionalHosts = hosts;
  }

  @action
  setHostAndUserData(changeset: DetailedChangeset<SignInFieldModel>, employee: EmployeeModel): void {
    // Employee is nil when host is cleared
    const employeeName = employee && employee.name;

    changeset.value = employeeName;
    this.updateUserDataField(changeset);

    const inviteChangeset = this.args.inviteChangeset;
    inviteChangeset.employee = <AsyncBelongsTo<EmployeeModel>>(<unknown>employee);
    inviteChangeset.inviterName = employeeName;
  }

  @action
  searchEmployees(term: string): TaskInstance<EmployeeModel[]> {
    const extraFilters = {
      locations: get(this.args.inviteChangeset.location, 'id'),
    };
    return this.searchEmployeesTask.perform(term, extraFilters);
  }

  @action
  setRrule(recurringRule: string): void {
    this.args.inviteChangeset.recurringRule = recurringRule;
  }

  @action
  handleGuestEmail({ target }: { target: HTMLInputElement }): void {
    this.sendGuestEmail = target.checked;
  }

  @action
  trackReadOnlyClicksOnly(invite_id: string): void {
    if (this.args.shouldShowRecurrence && this.args.isEditInvite) {
      this.metrics.trackEvent('Recurring Events - Read Only Expected Arrival Date/Time Clicked', { invite_id });
    }
  }

  @action
  didSelectTime(newTime: string): void {
    const timezone = get(this, 'timezone');
    let date;

    if (this.expectedArrivalTime) {
      date = timezone ? moment(this.expectedArrivalTime).tz(timezone) : moment(this.expectedArrivalTime);
    } else {
      date = timezone ? moment().tz(timezone) : moment();
    }
    const newDate = moment(newTime, 'h:mm a');

    date.hours(newDate.hours());
    date.minutes(newDate.minutes());
    get(this, 'setDate').call(this, date.toDate());
  }

  @action
  attachFileToDocument(
    visitorDocument: VisitorDocumentModel,
    userDocumentTemplateAttachment: UserDocumentTemplateAttachmentModel,
    update: File | string,
  ): void {
    const { inviteChangeset } = this.args;

    // This updates the changeset with minimal modification to underlying model data
    if (inviteChangeset) {
      const visDocs = A([...inviteChangeset.visitorDocuments.slice(), visitorDocument]).uniqBy('identifier');

      set(inviteChangeset, 'visitorDocuments', visDocs);
    }

    let userDocumentAttachment = visitorDocument.getAttachment(userDocumentTemplateAttachment.id);

    if (!userDocumentAttachment) {
      userDocumentAttachment = this.store.createRecord('user-document-attachment');
      userDocumentAttachment.userDocumentTemplateAttachment = userDocumentTemplateAttachment;
      userDocumentAttachment.visitorDocument = visitorDocument;
    }

    if (update instanceof File) {
      userDocumentAttachment.file = update;
    }

    if (typeof update === 'string') {
      userDocumentAttachment.fileUrl = update;
    }
  }

  @action
  resetVisitorDocument(visitorDocument: VisitorDocumentModel): void {
    const {
      hasRecurringInvite,
      args: { inviteChangeset, invite },
    } = this;

    if (visitorDocument.isNew || hasRecurringInvite) {
      const visDocs = A([...inviteChangeset.visitorDocuments.slice()]).without(visitorDocument);

      inviteChangeset.visitorDocuments = visDocs;
      invite.visitorDocuments = visDocs;

      if (<boolean>(<unknown>visitorDocument.isNew)) visitorDocument.unloadRecord();
    } else {
      this.confirmRemovalOfDocument = visitorDocument;
    }
  }

  saveVisitorDocumentsTask = task(
    async (invite: InviteModel | RecurringInvite, visitorDocuments: VisitorDocumentModel[]) => {
      return await all(
        visitorDocuments.map((visitorDocument) => this.saveVisitorDocumentTask.perform(invite, visitorDocument)),
      );
    },
  );

  saveVisitorDocumentTask = task(
    async (invite: InviteModel | RecurringInvite, visitorDocument: VisitorDocumentModel) => {
      if (!this.isVisitorDocumentPersistable(visitorDocument)) return visitorDocument;

      // Associate the invite's location to the visitor document (fallback to current location)
      const location =
        this.store.peekRecord('location', invite.belongsTo('location').id()) || this.state.currentLocation;
      const association = <'invites' | 'recurringInvites'>(
        pluralize(camelize((<{ modelName: string }>(<unknown>invite.constructor)).modelName))
      );

      visitorDocument.locations = A([location]);
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      visitorDocument[association].content.addObject(invite);

      await this.uploadVisitorDocumentAttachmentsTask.perform(invite, visitorDocument);
      await visitorDocument.save();

      // Cleanup attachments

      visitorDocument.userDocumentAttachments.content
        .filter((attachment) => attachment.isNew)
        .map((attachment) => attachment.unloadRecord());

      return visitorDocument;
    },
  );

  uploadVisitorDocumentAttachmentsTask = task(
    async (invite: InviteModel | RecurringInvite, visitorDocument: VisitorDocumentModel) => {
      const { activeStorageExtension } = this;
      const directUploadURL = '/a/visitors/api/direct-uploads';
      const userDocumentTemplateId = visitorDocument.belongsTo('userDocumentTemplate').id();

      if (!(invite?.id && userDocumentTemplateId)) return;

      const pathSegment = underscore(pluralize((<{ modelName: string }>(<unknown>invite.constructor)).modelName));
      const prefix = `user-documents/${userDocumentTemplateId}/${pathSegment}/${invite.id}`;
      const endpoint = `${directUploadURL}?prefix=${prefix}`;

      return await all(
        visitorDocument.userDocumentAttachmentsPendingUpload.map((userDocumentAttachment) =>
          this.uploadDocumentAttachmentTask.perform(userDocumentAttachment, endpoint, activeStorageExtension),
        ),
      );
    },
  );

  @task uploadDocumentAttachmentTask = {
    progress: 0,

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    *perform(
      userDocumentAttachment: UserDocumentAttachmentModel,
      endpoint: string,
      activeStorageExtension: ActiveStorageService,
    ) {
      const { file } = userDocumentAttachment;

      if (!(file instanceof File)) {
        throw new Error('Upload halted: no file specified');
      }

      if (!endpoint) {
        throw new Error('Upload halted: no direct upload endpoint specified');
      }

      if (typeof activeStorageExtension.upload !== 'function') {
        throw new Error('Upload halted: invalid Active Storage service specified');
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { signedId } = yield activeStorageExtension.upload(file, endpoint, {
        onProgress: (progress: number) => {
          this.progress = progress;
          if (!userDocumentAttachment.isDestroyed && !userDocumentAttachment.isDestroying) {
            userDocumentAttachment.uploadProgress = progress;
          }
        },
      });

      userDocumentAttachment.file = <string>signedId;

      return <string>signedId;
    },
  };

  removeVisitorDocumentFromInviteTask = task(async (visitorDocument: VisitorDocumentModel) => {
    const { userDocumentTemplate } = visitorDocument;
    const { inviteChangeset } = this.args;

    const visitorDocuments = A(inviteChangeset.visitorDocuments.slice());

    try {
      await visitorDocument.removeFromInvites([inviteChangeset]);

      visitorDocuments.removeObject(visitorDocument);
      visitorDocuments.push(
        this.store.createRecord('visitor-document', { userDocumentTemplate: userDocumentTemplate.content }),
      );

      inviteChangeset.visitorDocuments = visitorDocuments;
      this.flashMessages.showAndHideFlash('success', `${visitorDocument.title} removed from invite`);
    } catch (_) {
      const template = userDocumentTemplate.content;
      if (template) visitorDocuments.removeObject(inviteChangeset.visitorDocumentForTemplate(template)!);
      visitorDocuments.addObject(visitorDocument);

      inviteChangeset.visitorDocuments = visitorDocuments;
      this.flashMessages.showFlash('error', `Failed to remove ${visitorDocument.title} from invite`);
    }
  });

  saveBlocklistFilterTask = task(
    waitFor(async (blocklistFilter: BlacklistFilter, photo: File | null) => {
      try {
        await saveBlocklistFilter({
          blocklistFilter,
          failOnPhotoFetchError: this.featureFlags.isEnabled('blocklist-fail-on-photo-fetch-fail'),
          photo,
          uploaderFactory: this.uploaderFactory,
        });
        this.blocklistFilterInModal = undefined;
        this.flashMessages.showAndHideFlash('success', 'Blocklist filter saved');
      } catch (e) {
        this.flashMessages.showFlash('error', parseErrorForDisplay(e));
      }
    }),
  );

  @action
  createBlocklistFilter(): void {
    this.blocklistFilterInModal = createBlocklistFilter({
      model: this.args.invite,
      store: this.store,
      location: this.state.currentLocation,
      useLocationScoping: this.featureFlags.isEnabled('feat-blocklist-location-scoping'),
    });

    this.metrics.startJob();

    this.metrics.trackJobEvent('Entry - Add to Block List Button Clicked', {
      entry_id: this.args.invite.id,
    });
  }

  @action
  emailOnFocusIn(): void {
    this.emailHasFocus = true;
  }

  @action
  emailOnFocusOut(): void {
    this.emailHasFocus = false;
  }

  @action
  inviteAgain(invite: InviteModel): void {
    const { visitorEmail: email, fullName: name, id: previousInvite } = invite;
    const qpEmail = email || invite.email; // Fallback to email param

    void this.router.transitionTo('visitors.invites.new', {
      queryParams: { email: qpEmail, name, previousInvite },
    });

    this.metrics.trackEvent('Invites Invited Again', { invite_id: previousInvite });
  }

  @action
  pollInviteReload(): void {
    cancel(this.pollTimer);

    const snapshot = JSON.stringify(this.args.invite.userData ?? {});
    const snapshotNames = new Set(
      ((this.args.invite.userData ?? A()) as UserDatum[]).map((d) => d.field).filter((name): name is string => !!name),
    );
    let attempts = 0;

    const poll = () => {
      attempts++;
      void this.args.invite
        .reload()
        .then(() => {
          run(() => {
            if (this.isDestroying || this.isDestroyed) return;
            const current = JSON.stringify(this.args.invite.userData ?? {});
            const changed = current !== snapshot;
            if (changed) {
              const newFieldNames = ((this.args.invite.userData ?? A()) as UserDatum[])
                .map((d) => d.field)
                .filter((name): name is string => !!name && !snapshotNames.has(name));
              void this.pollSignInFieldsRefresh(newFieldNames);
              return;
            }
            if (attempts >= 5) return;
            this.pollTimer = later(poll, 1000);
          });
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.error('[pollInviteReload] reload failed, stopping poll', err);
        });
    };

    this.pollTimer = later(poll, 1000);
  }

  private async pollSignInFieldsRefresh(newFieldNames: string[]): Promise<void> {
    if (newFieldNames.length === 0) {
      // No new fields — existing fields got new values, bust the cache so they render
      notifyPropertyChange(this, 'userDataChangesets');
      return;
    }

    const includes = [
      'sign-in-field-actions',
      'actionable-sign-in-field-actions',
      'actionable-sign-in-fields',
      'flow-version',
    ].join(',');
    let sfAttempts = 0;

    const pollFields = async (): Promise<void> => {
      sfAttempts++;

      let freshFields: SignInFieldModel[];
      try {
        const result = await this.store.query('invite-sign-in-field', {
          inviteId: this.args.invite.id,
          include: includes,
        });
        // invite-sign-in-field records are typed as SignInFieldModel at the component boundary
        freshFields = result.slice() as unknown as SignInFieldModel[];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[pollSignInFieldsRefresh] query failed, stopping', err);
        return;
      }

      if (this.isDestroying || this.isDestroyed) return;

      const freshNames = new Set(freshFields.map((f) => f.name));
      const allPresent = newFieldNames.every((name: string) => freshNames.has(name));

      if (allPresent) {
        run(() => {
          if (this.isDestroying || this.isDestroyed) return;
          this.freshSignInFields = freshFields;
          notifyPropertyChange(this, 'signInFields');
        });
        return;
      }

      if (sfAttempts >= 5) return;

      this.pollSignInFieldsTimer = later(() => void pollFields(), 1000);
    };

    await pollFields();
  }

  @action
  async injectComponent(): Promise<void> {
    const { ApprovalChainDetailsCard } = await import('reactRichComponents/visitors/ApprovalChainDetailsCard');

    // prevent error from being thrown if element is already destroyed from navigating away
    if (!document.getElementById('react__approval-chain-details-card')) {
      return;
    }

    // For secondary (child) invites, parentInviteContext.id is the primary invite's ID.
    // The workflow is created on the primary invite — use its ID so the AVA box fetches
    // the shared workflow instead of getting a 404.
    const workflowInviteId = this.args.invite.belongsTo('parentInviteContext').id() ?? this.args.invite.id;

    // For recurring-series children, only the first child has an invite->workflow
    // mapping in WTA; siblings share the same workflow but would 404 on inviteId
    // lookup. workflowId (from envoy-web's ava_workflow_id attribute) lets RRC
    // resolve via GET /v1/workflows/:workflowId. RRC precedence is
    // workflowId > inviteId > entryId, so passing both is safe.
    const workflowId = this.args.invite.avaWorkflowId ?? undefined;

    this.root = await this.reactRenderer.render(ApprovalChainDetailsCard, 'react__approval-chain-details-card', {
      workflowId,
      inviteId: workflowInviteId,
      employeeId: this.currentAdmin.id,
      readOnly: false,
      visitModified: Boolean(
        this.args.invite.visitApproval?.employeeTriggeredRescreen ||
          this.args.invite.visitApproval?.visitorTriggeredRescreen,
      ),
      onDataCollectionComplete: () => this.pollInviteReload(),
    });
  }

  @action
  destroyComponent(): void {
    cancel(this.pollTimer);
    this.pollTimer = undefined;
    cancel(this.pollSignInFieldsTimer);
    this.pollSignInFieldsTimer = undefined;
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
  }
}
